import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { PaginatorModule } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { forkJoin } from 'rxjs';

import { SessionService } from '../../../auth/services/session/session.service';
import { LayoutComponent } from '../../../shared/layouts/layout/layout.component';
import { LoadingService } from '../../../shared/services/loading/loading.service';
import { AreaPhotosResponse, AreasResponse } from '../../models/areas.model';
import type { Event as EventType, Service } from '../../models/events.model';
import { AreasService } from '../../services/areas/areas.service';
import { EventsService } from '../../services/events/events.service';

interface Area {
  id: string;
  station: string;
  station_activity: string;
  station_type_activity: string;
  station_condition: string;
  plagues: {
    plague: string;
    count: number;
  }[];
  activity_in_station: string;
  employee_activity: string;
  photos: string[];
}

@Component({
  selector: 'app-areas',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    DropdownModule,
    LayoutComponent,
    TableModule,
    ButtonModule,
    PaginatorModule,
  ],
  templateUrl: './areas.component.html',
  styleUrl: './areas.component.scss'
})
export class AreasComponent implements OnInit {
  private _loadingService = inject(LoadingService);
  private _eventsService = inject(EventsService);
  private _areasService = inject(AreasService);
  private _formBuilder: FormBuilder = inject(FormBuilder);
  private _sessionService = inject(SessionService);

  public _isModalVisible = false;
  public dataForm!: FormGroup;
  public dataFormModal!: FormGroup;
  public loadingServices = false;
  public loadingAreas = false;
  public isPhotoModalVisible = false;

  public activeService: Service | null = null;
  public services: Service[] = [];
  public areas: Area[] = [];
  public selectedPhotos: string[] = [];

  ngOnInit(): void {
    this.loadDataForm();
    this.loadServices();
  }

  public get isBlocked(): boolean {
    return this._sessionService.CompanyStatus !== 'company-paid';
  }

  public get isModalVisible(): boolean {
    return this._isModalVisible;
  }

  public set isModalVisible(value: boolean) {
    this._isModalVisible = value;

    if (!value) {
      this.activeService = null;
    }
  }

  loadDataForm(): void {
    this.dataForm = this._formBuilder.group({
      totalRecords: 0,
      page: 1,
      page_size: 10,
      firstPage: 0,
      search: '',
      status__name: 'Finalizado'
    });

    this.dataFormModal = this._formBuilder.group({
      totalRecords: 0,
      page: 1,
      page_size: 10,
      firstPage: 0,
      search: '',
      event: '',
    });
  }

  loadServices(): void {
    this.loadingServices = true;

    this._eventsService.getEvents(this.dataForm.value).subscribe({
      next: (data: {
        items: EventType[];
        total: number;
        page: number;
        page_size: number;
      }) => {
        this.services = data.items.map((service) => ({
          ...service,
          employee: service.employee[0],
        }));

        this.dataForm.value.totalRecords = data.total;
        this.dataForm.value.page = data.page;
        this.dataForm.value.page_size = data.page_size;

        this.loadingServices = false;
      },
      error: (err: any) => {
        console.error('Error al cargar servicios:', err);
        this.loadingServices = false;
      },
    });
  }

  handleOpenModal(service: Service): void {
    if (!this.activeService) {
      this._loadingService.show();
    }

    this.activeService = service;
    this.loadingAreas = true;

    this.dataFormModal.value.event = service.id;

    this._areasService.getAreas(this.dataFormModal.value).subscribe({
      next: (data: { items: AreasResponse[], total: number, page: number, page_size: number }) => {
        this.areas = data.items.map((area: AreasResponse) => ({
          id: area.id,
          station: area.station.name,
          station_activity: area.station_activity.map((activity: any) => activity.name).join(', '),
          station_type_activity: area.stationTypeActivity.map((activity: any) => activity.name).join(', '),
          station_condition: area.station_condition.map((condition: any) => condition.name).join(', '),
          plagues: area.plague_counts.map((plague: any) => ({
            plague: plague.plague.name,
            count: plague.count_plague,
          })),
          activity_in_station: area.activityinStation ?? '',
          employee_activity: area.EmployeeActivity ?? '',
          photos: []
        }));

        if (this.areas.length === 0) {
          this._loadingService.hide();
          this.loadingAreas = false;
          this._isModalVisible = true;

          return;
        }

        const photoRequests = this.areas.map((area) =>
          this._areasService.getAreasPhotos(area.id)
        );

        forkJoin(photoRequests).subscribe({
          next: (photosResponses: AreaPhotosResponse[][]) => {
            photosResponses.forEach((photos, index) => {
              this.areas[index].photos = photos.map((photo) => photo.photo);
            });

            this.dataFormModal.value.totalRecords = data.total;
            this.dataFormModal.value.page = data.page;
            this.dataFormModal.value.page_size = data.page_size;

            this._loadingService.hide();
            this._isModalVisible = true;
            this.loadingAreas = false;
          },
          error: (err) => {
            console.error('Error al cargar fotos:', err);
            this.loadingAreas = false;
            this._isModalVisible = true;
          }
        });
      },
      error: (err: any) => {
        console.error('Error al cargar los datos:', err);
        this.loadingAreas = false;
      }
    });
  }

  openPhotoModal(photos: string[]): void {
    this.selectedPhotos = photos;
    this.isPhotoModalVisible = true;
  }

  pageChange(event: any) {
    const newPage = event.first / event.rows;

    this.dataForm.value.firstPage = event.first;
    this.dataForm.value.page = newPage + 1;

    this.loadServices();
  }

  pageChangeModal(event: any) {
    const newPage = event.first / event.rows;

    this.dataFormModal.value.firstPage = event.first;
    this.dataFormModal.value.page = newPage + 1;

    this.handleOpenModal(this.activeService!);
  }
}
